import React, { Component, ReactNode } from "react";
import { Button } from "react-md";
import { Card, CardTitle, CardText } from "react-md/lib/Cards";
import MDialog from "components/Shared/MDialog";

export interface MSectionProps {
  titleClassName?: string, // class name that will be appended onto the card title if provided
  className?: string, // class name that will be appended onto the card if provided
  title: string | ReactNode, // title that displays on the header
  children: ReactNode, // content to display in side of the collapsible pane
  defaultExpanded?: boolean, // if true the pane will be expanded on mount, if false it will not be expanded
  /* this callback function will be called with an object that has multiple functions on it that let you control MSection
    {
      toggleExpanded(expanded) if expanded is not provided this will just toggle, otherwise if true is passed it will expand, if false is passed it will collapse
      isExpanded returns true if the section is expanded, false otherwise
    } */
  getControlMethods?: (methods: { toggleExpanded: (expanded?: boolean, forceToggle?: boolean) => void, isExpanded: () => boolean }) => void,
  collapsible?: boolean, // defaults to true, if false the section will not be collapsible
  onExpandToggled?: (expanded: boolean) => void, // called with boolean for if it is newly expanded or not
  fullHeight?: boolean, // will make the section render 100% height of its container and its contents will scroll
  cardClassName?: string, // Allows specifying a class for the card text, useful when we need different styles applied across tabbed vs vertical views. 
  canUnmountChildWarning?: string,  // for the canUnmountChildStatus logic below
  /*
  one of UNMOUNT_STATUS_ALLOW, UNMOUNT_STATUS_DISALLOW, UNMOUNT_STATUS_WARNING
  when collapsing msection the child component will be unmounted. the user of the control may want to block this in case there are unsaved changes in the form or some other reasons
  if UNMOUNT_STATUS_ALLOW is the default if nothing is specified and the user will be able to navigate away from the child component
  if UNMOUNT_STATUS_DISALLOW the unmount action simply will not happen. a dialog with canUnmountStatus will be rendered if specified
  if UNMOUNT_STATUS_WARNING the user will be presented with the option to still navigate away. the canUnmountStatus message will be displayed
  */
  canUnmountChildStatus?: string,
  onChildUnmount?: () => void, // optional - called when the child is unmounted

  visible?: boolean, // Prop to indicate whether or not this comp should be visisble. Helps support persistant tabs
  visited?: boolean, // When using persistant tabs, each MSection is not rendered until it's been visited. MSectionCollection tracks this. 
}

export interface State {
  expanded: boolean,
  showUnmountDialog: boolean,
}

class MSection extends Component<MSectionProps, State> {

  static defaultProps = {
    defaultExpanded: true,
    collapsible: true,
    fullHeight: false,
    cardClassName: "",
    visible: true,
    visited: true,
  };
  
  constructor(props: MSectionProps) {
    super(props);

    this.toggleExpanded = this.toggleExpanded.bind(this);
    this.showDialog = this.showDialog.bind(this);
    this.getDialogButtons = this.getDialogButtons.bind(this);

    this.state = {
      expanded: props.defaultExpanded,
      showUnmountDialog: false,
    };
  }

  componentDidMount() {
    if (this.props.getControlMethods) {
      this.props.getControlMethods({
        toggleExpanded: this.toggleExpanded,
        isExpanded: () => this.state.expanded
      });
    }
  }

  toggleExpanded(expanded?: boolean, forceToggle?: boolean) {
    //have to check for typeof boolean because the onExpanderClicked Card methods passes the event in for a param
    if (expanded === undefined || typeof (expanded) !== "boolean") expanded = !this.state.expanded;

    if (!forceToggle && this.state.expanded && !expanded && this.props.canUnmountChildWarning &&
      (this.props.canUnmountChildStatus === UNMOUNT_STATUS_DISALLOW || this.props.canUnmountChildStatus === UNMOUNT_STATUS_WARNING)) {
      this.showDialog();
    } else {
      this.setState({ expanded: expanded });
      if (this.props.onExpandToggled) this.props.onExpandToggled(expanded);
    }
  }

  showDialog(show?: boolean) {
    if (show === undefined) show = true;
    this.setState({ showUnmountDialog: show });
  }

  getDialogButtons() {
    if (this.props.canUnmountChildStatus === UNMOUNT_STATUS_WARNING) {
      return [
        <Button key="yes" primary raised onClick={() => {
          this.showDialog(false);
          this.toggleExpanded(false, true);
          if (this.props.onChildUnmount) this.props.onChildUnmount();
        }}>Yes</Button>,
        <Button key="no" primary raised onClick={() => this.showDialog(false)}>No</Button>
      ];
    } else {
      return [<Button key="ok" primary raised onClick={() => this.showDialog(false)}>Ok</Button>];
    }
  }

  render() {
    return (
      this.props.visited ?
        <>
          {this.state.showUnmountDialog && (
            <MDialog contentActions={this.getDialogButtons()}>
              {this.props.canUnmountChildWarning}
            </MDialog>)}
          <Card
            style={{ display: this.props.visible ? "" : "none" }}
            className={"msection " + (this.props.className || "") + (this.props.fullHeight ? "msection-full-height" : "")}
            expanded={this.state.expanded} onExpanderClick={this.toggleExpanded} >
            <CardTitle
              {...this.props.titleClassName && { className: this.props.titleClassName }}
              title={this.props.title}
              style={{ cursor: "pointer" }}
              onClick={() => this.toggleExpanded()}
              expander={this.props.collapsible} />
            <CardText expandable={this.props.collapsible} className={`msection-card-text ${this.props.cardClassName}`}>
              {this.props.children}
            </CardText>
          </Card>
        </> : null);
  }
}

export const UNMOUNT_STATUS_ALLOW = "UNMOUNT_STATUS_ALLOW";
export const UNMOUNT_STATUS_DISALLOW = "UNMOUNT_STATUS_DISALLOW";
export const UNMOUNT_STATUS_WARNING = "UNMOUNT_STATUS_WARNING";

export default MSection;